import React, {
  ReactElement,
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Flex, Text } from 'rebass/styled-components';

import useFormsColumns from './useFormsColumns';

import { MainContent, PageContent, PageHeader, Table } from 'ui/patterns';

import { useGetFormsList } from 'hooks';
import { useDocumentTitle, useGetAccountInfo } from 'hooks';

import * as Icon from '../../assets/icons';
import { SortItemType } from 'types/modalTypes';
import { FormListType } from './formListType';

const pageTitle = 'List of Account Forms';

const sortItem = [
  { sortKey: 'Date Last Updated', sortOrder: 'desc' },
  { sortKey: 'Form Status', sortOrder: 'desc' },
];

export default function FormsList({
  ...StyleProps
}: FormListType): ReactElement {
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState<
    SortItemType | Array<SortItemType>
  >(sortItem);
  const [keywordSearch, setKeywordSearch] = useState(''); // keyword search

  useDocumentTitle('Account Forms', true);

  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;

  const history = useHistory<{ ID: number; formId: number }>();

  const { accountId: passedId } = useParams<{
    accountId: string;
  }>();

  const accountId = parseInt(passedId);

  const { data: accountInfo, isLoading: isAccountInfoLoading } =
    useGetAccountInfo(accountId);

  const {
    data: formList,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useGetFormsList({
    accountId: accountId,
    keywordSearch,
    columnsSearch,
    sortItem: currentSortItem,
  });

  const tableData = useMemo(() => {
    if (formList) {
      return formList?.pages?.map((dataItem: any) => dataItem.Data).flat();
    }
  }, [formList]);

  const totalItems = useMemo(() => {
    if (formList) {
      return formList.pages[0].FilteredRecords;
    }
    return 0;
  }, [formList]);

  const onEndReached = () => {
    if (tableData) {
      if (hasNextPage && tableData?.length < totalItems) fetchNextPage();
    }
  };
  const columns = useFormsColumns();

  const accountName = isAccountInfoLoading
    ? 'Processing'
    : `${accountInfo?.accountInfoReturn.Data.Name} `;
  const pageSubTitle = !isAccountInfoLoading
    ? `${accountInfo?.accountInfoReturn.Data.CID}${'-'}${
        accountInfo?.accountInfoReturn.Data.SID
      }${'-'}${accountInfo?.accountInfoReturn.Data.PID}`
    : null;

  useEffect(() => {
    if (accountId === undefined) {
      history.push('/home');
    }
  });

  function switchTab(tabId: string, formId?: string | number) {
    history.push(
      `/home/accounts/edit/${accountId}/${tabId}${formId ? `/${formId}` : ''}`,
    );
  }

  const pageActions = [
    {
      action: 'Reset',
      icon: <Icon.Refresh title="" ariaHidden="true" />,
      handleButtonClick: () => handleReset(),
    },
    {
      action: 'Add New Form',
      icon: <Icon.Add title="" ariaHidden="true" />,
      handleButtonClick: () => {
        switchTab && switchTab('AddForm');
      },
    },
  ];

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (currentSortItem instanceof Array) {
        const existingItem = currentSortItem.find(
          sItem => sItem.sortKey === item,
        );
        if (existingItem) {
          setCurrentSortItem({
            sortKey: item,
            sortOrder: existingItem.sortOrder === 'asc' ? 'desc' : 'asc',
          });
        } else {
          setCurrentSortItem({
            sortKey: item,
            sortOrder,
          });
        }
      } else if (currentSortItem.sortKey && currentSortItem.sortOrder) {
        if (
          currentSortItem.sortKey === item &&
          currentSortItem.sortOrder === 'asc'
        ) {
          sortOrder = 'desc';
        }
        setCurrentSortItem({
          sortKey: item,
          sortOrder,
        });
      }
    },
    [setCurrentSortItem, currentSortItem],
  );

  const handleKeywordSearch = (value: string): void => {
    setKeywordSearch(value);
  };

  const handleReset = (): void => {
    setKeywordSearch('');
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
  };

  const handleEditForm = (row: any): void => {
    if (switchTab) switchTab('EditForm', row.original.Id);
  };

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  return (
    <MainContent flexDirection="column" m={-4} {...StyleProps}>
      <PageHeader
        title={pageTitle || ''}
        subTitle={`${accountName}(${pageSubTitle})`}
        SubtitleTootip={`${accountName}(${pageSubTitle})`}
        actions={pageActions}
        hasSearch
        searchValue={keywordSearch}
        handleChange={handleKeywordSearch}
      />
      <PageContent>
        <Text mb={2} fontSize={1} color="text.body" sx={{ height: '20px' }}>
          {tableData && tableData.length > 0 && (
            <Text mb={2}>{`Showing ${tableData.length} of ${totalItems}${
              isFetching ? ' | Processing' : ''
            }`}</Text>
          )}
        </Text>
        <Table
          columns={columns}
          data={tableData}
          isLoading={isLoading || isFetching}
          headerUpdateCallBack={headerUpdateCallBack}
          handleSortChange={handleSortChange}
          multiSortedItem={currentSortItem}
          columnsSearch={columnsSearch}
          onRowDoubleClick={handleEditForm}
          onEditPress={(id: number) => {
            if (switchTab) switchTab('EditForm', id);
          }}
          hasInlineLoader
          onEndReached={onEndReached}
          isFetching={!!isFetchingNextPage}
        />
        <Flex mt={4} justifyContent="flex-end"></Flex>
      </PageContent>
    </MainContent>
  );
}
